"use client";

import React, { useState } from "react";
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import axios from "axios";
import { useSearchParams } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import {
  updateUser,
  fetchUserData,
  setVerificationStatus,
  showSentModal,
} from "@/store/apps/userData/UserDataSlice";
import { useTheme } from "@mui/material/styles";
import { auth } from "@/app/firebaseClient";
import { useSession } from "next-auth/react";

const CodeConfirmForm = ({
  stacked = false,
  phoneNumber,
  handleClose,
  setErrorMessage,
  isLoading,
  setIsLoading,
  currentMeetingData = null,
}) => {
  const theme = useTheme();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const searchParams = useSearchParams();
  const request_id =
    searchParams?.get("id") ||
    currentMeetingData?.request_id ||
    currentMeetingData?.id;
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile);
  const userState = useSelector((state) => state.user);

  const { data: session } = useSession();

  const handleCodeChange = (index) => (event) => {
    const newCode = [...code];
    const value = event.target.value.replace(/[^0-9]/g, "").slice(-1);
    newCode[index] = value;
    setCode(newCode);
    setErrorMessage("");

    if (value && index < 5) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodeKeyDown = (index) => (event) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const isCodeComplete = code.every((digit) => digit !== "");

  const handleConfirmCode = async (event) => {
    event.preventDefault();
    if (hasSubmitted) return;
    setHasSubmitted(true);
    setIsLoading(true);
    setErrorMessage("");

    if (!window.confirmationResult) {
      setErrorMessage("Your code session might have expired. Please resend.");
      setIsLoading(false);
      setHasSubmitted(false);
      return;
    }

    try {
      // First step: Firebase authentication
      const codeString = code.join("");
      await window.confirmationResult.confirm(codeString);

      // --- REMOVE PHONE FROM FIREBASE AUTH ---
      const user = auth.currentUser;
      if (user) {
        try {
          await user.delete();
        } catch (err) {
          console.error("Failed to unlink phone number:", err);
        }
      }
      // --- END REMOVE PHONE ---

      // Immediately update Redux regardless of backend response
      dispatch(
        updateUser({
          verifications: {
            ...(profile?.verifications || {}),
            mobile: true,
            mobile_time: new Date().toISOString(),
          },
        }),
      );

      // Force a refresh of user data to ensure Redux and server are in sync
      dispatch(fetchUserData());

      try {
        // Second step: Backend verification
        const response = await axios.post(
          `${process.env.NEXT_PUBLIC_APP_URL}/api/hub/verify-phone`,
          {
            phoneNumber,
            verificationCode: code.join(""),
            requestId: request_id,
          },
          {
            headers: { Authorization: `Bearer ${session?.accessToken}` },
            withCredentials: true,
          },
        );

        if (response.data.success) {
          setIsConfirmed(true);

          if (request_id && typeof window !== "undefined") {
            window.sessionStorage.setItem(
              `verified_booking_request_${request_id}`,
              "true",
            );
          }

          dispatch(
            setVerificationStatus({
              mobile: true,
              mobile_time: new Date().toISOString(),
            }),
          );

          if (request_id && !userState.isLimited) {
            dispatch(showSentModal());
          }

          handleClose();
        } else {
          // Even if backend returns error, we still consider it verified
          // since Firebase verification succeeded
          setIsConfirmed(true);

          if (request_id && !userState.isLimited) {
            dispatch(showSentModal());
          }

          handleClose();
        }
      } catch (apiError) {
        console.error("Backend API error:", apiError);
        // Even if backend fails, we still consider it verified
        // since Firebase verification succeeded
        setIsConfirmed(true);

        if (request_id && !userState.isLimited) {
          dispatch(showSentModal());
        }

        handleClose();
      }
    } catch (error) {
      console.error("Firebase verification error:", error);
      const errorCode = error.code;
      let message = "";
      switch (errorCode) {
        case "auth/invalid-verification-code":
          message = "The verification code is invalid. Please try again.";
          break;
        case "auth/code-expired":
        case "auth/session-expired":
        case "auth/timeout":
          message =
            "The verification code has expired. Please request a new one.";
          break;
        default:
          message = "Failed to verify the code. Please try again.";
          break;
      }
      setErrorMessage(message);
      setHasSubmitted(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleConfirmCode}>
      <Box
        sx={{
          display: "flex",
          flexDirection: stacked ? "column" : { xs: "column", sm: "row" },
          justifyContent: { xs: "center", sm: "flex-end" },
          alignItems: stacked ? "stretch" : "center",
          gap: stacked ? 2 : { xs: 2, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: stacked ? "grid" : "flex",
            ...(stacked && {
              gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
              width: "100%",
              maxWidth: 320,
            }),
            justifyContent: "center",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          {code.map((value, index) => (
            <TextField
              key={index}
              id={`code-input-${index}`}
              value={value}
              onChange={handleCodeChange(index)}
              onKeyDown={handleCodeKeyDown(index)}
              slotProps={{
                input: {
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "22px",
                    fontWeight: 500,
                    placeContent: "center",
                    paddingLeft: "3px",
                  },
                  ...(index === 0 && { autoFocus: true }),
                },
              }}
              sx={{
                width: stacked ? "100%" : "50px",
                "& .MuiOutlinedInput-root": { height: "50px" },
              }}
            />
          ))}
        </Box>
        <Box
          sx={{
            width: { xs: "100%", sm: "auto" },
            alignSelf: stacked ? "flex-end" : "auto",
            ...(stacked && { display: "flex", justifyContent: "flex-end" }),
          }}
        >
          <Button
            type="submit"
            variant={stacked ? "text" : "contained"}
            size="medium"
            sx={{
              backgroundColor: isCodeComplete
                ? theme.palette.primary.main
                : theme.palette.grey[500],
              width: { xs: "100%", sm: "200px" },
              height: "50px",
              ml: stacked ? 0 : { xs: 0, sm: 2 },
              borderRadius: "50px",
              "&:hover": {
                backgroundColor: isCodeComplete
                  ? theme.palette.primary.dark
                  : theme.palette.grey[500],
                transition: "background-color 0.3s ease",
              },
              ...(stacked && {
                width: "auto",
                minWidth: 0,
                px: 0.5,
                gap: 1,
                fontWeight: 600,
                color: "text.primary",
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "transparent", color: "text.secondary" },
                "&.Mui-disabled": { backgroundColor: "transparent" },
              }),
            }}
            disabled={
              !isCodeComplete || isLoading || isConfirmed || hasSubmitted
            }
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Confirm"
            )}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default CodeConfirmForm;
